import React, { useState, useRef, useContext, forwardRef, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page } from 'react-pdf';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { GatefoldView } from './GatefoldView';
import { UnfoldableFlipPage } from './UnfoldableFlipPage';
import { CenteredUnfoldIcon } from './CenteredUnfoldIcon';
import { usePdfLoader } from '../../hooks/usePdfLoader';
import { LanguageContext } from '../../contexts/Language';
import { t } from '../../contexts/Language/reducer';
import { PortfolioPage, isSinglePage, isUnfoldPage, UnfoldLayout } from '../../types/portfolio';

// Animation timing constants - must match CSS animation duration
const GATEFOLD_ANIMATION_DURATION = 400; // matches new CSS animation
const FLIPBOOK_FADE_DURATION = 150; // reduced for quicker transition

interface GatefoldState {
  isActive: boolean;
  pdfs: string[];
  returnToPage: number;
  transitionPhase: 'idle' | 'closing-flipbook' | 'opening-gatefold' | 'active' | 'closing-gatefold' | 'opening-flipbook';
}

/**
 * Expanded page type for rendering
 * Unfold pages are expanded into multiple FlipPage elements
 */
interface ExpandedPage {
  type: 'single' | 'unfold-left' | 'unfold-right';
  url: string;
  unfoldData?: {
    pdfs: string[];
    layout: UnfoldLayout;
  };
}

/**
 * Expands portfolio pages for flipbook rendering
 * Converts UnfoldPage into multiple pages (left and right) to show in flipbook
 */
function expandPages(pages: PortfolioPage[]): ExpandedPage[] {
  const expanded: ExpandedPage[] = [];

  for (const page of pages) {
    if (isSinglePage(page)) {
      expanded.push({ type: 'single', url: page.url });
    } else if (isUnfoldPage(page)) {
      if (page.layout === '1x2') {
        // 1x2: Show both PDFs as regular pages, no unfold capability
        expanded.push({ type: 'single', url: page.pdfs[0] });
        expanded.push({ type: 'single', url: page.pdfs[1] });
      } else {
        // 1x3/1x4/2x2: First 2 PDFs in flipbook with unfold icon on right
        expanded.push({
          type: 'unfold-left',
          url: page.pdfs[0],
          unfoldData: { pdfs: page.pdfs, layout: page.layout }
        });
        expanded.push({
          type: 'unfold-right',
          url: page.pdfs[1],
          unfoldData: { pdfs: page.pdfs, layout: page.layout }
        });
      }
    }
  }

  return expanded;
}

const FlipPage = forwardRef<HTMLDivElement, { pdfUrl?: string; pageNumber: number; width: number; density?: 'hard' | 'soft'; isBlank?: boolean }>(
  ({ pdfUrl, pageNumber, width, density, isBlank }, ref) => (
    <div
      ref={ref}
      className="bg-white shadow-lg flex items-center justify-center overflow-hidden"
      data-density={density}
    >
      {isBlank ? (
        <div style={{ width, height: width * 1.414 }} />
      ) : (
        <Document file={pdfUrl!}>
          <Page
            pageNumber={pageNumber}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div className="flex items-center justify-center" style={{ width, height: width * 1.414 }}>
                <span className="text-gray-500">Loading page...</span>
              </div>
            }
            error={
              <div className="flex items-center justify-center bg-gray-100" style={{ width, height: width * 1.414 }}>
                <span className="text-red-500">Error loading page</span>
              </div>
            }
          />
        </Document>
      )}
    </div>
  )
);
FlipPage.displayName = 'FlipPage';

interface PdfFlipbookProps {
  pdfUrls: string[] | PortfolioPage[]; // Support both old and new formats
}

/**
 * Desktop flipbook component with page-turning animations
 * Uses react-pageflip for interactive book experience
 */
export const PdfFlipbook: React.FC<PdfFlipbookProps> = ({ pdfUrls }) => {
  const [languageState] = useContext(LanguageContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [gatefoldState, setGatefoldState] = useState<GatefoldState>({
    isActive: false,
    pdfs: [],
    returnToPage: 0,
    transitionPhase: 'idle',
  });
  const flipBookRef = useRef<any>(null);
  const [visibleUnfoldData, setVisibleUnfoldData] = useState<{
    layout: UnfoldLayout;
    pdfs: string[];
  } | null>(null);

  // Normalize input to PortfolioPage format
  const pages: PortfolioPage[] = React.useMemo(() => {
    if (pdfUrls.length === 0) return [];

    // Check if already in new format
    if (typeof pdfUrls[0] === 'object' && 'type' in pdfUrls[0]) {
      return pdfUrls as PortfolioPage[];
    }

    // Convert old format (string[]) to new format
    return (pdfUrls as string[]).map(url => ({ type: 'single', url }));
  }, [pdfUrls]);

  // Expand pages for flipbook rendering
  const expandedPages = React.useMemo(() => {
    return expandPages(pages.slice(1)); // Skip cover page
  }, [pages]);

  // Extract all PDF URLs for loader
  const allPdfUrls = React.useMemo(() => {
    return pages.flatMap(page =>
      isSinglePage(page) ? [page.url] : page.pdfs  // Use pdfs array, not preview
    );
  }, [pages]);

  const { loading, error } = usePdfLoader(allPdfUrls);

  // Calculate dimensions based on viewport (80% of viewport height, A4 ratio for width)
  const calculateDimensions = () => {
    const viewportHeight = window.innerHeight;
    const height = Math.floor(viewportHeight * .8);
    const width = Math.floor(height / 1.414); // A4 aspect ratio
    return { width, height };
  };

  const [dimensions, setDimensions] = useState(calculateDimensions);
  const pageWidth = dimensions.width;
  const pageHeight = dimensions.height;

  useEffect(() => {
    const handleResize = () => {
      setDimensions(calculateDimensions());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check initial page state for unfold pages
  useEffect(() => {
    if (flipBookRef.current && expandedPages.length > 0) {
      const initialPageIndex = flipBookRef.current.pageFlip().getCurrentPageIndex();
      const rightExpandedIndex = initialPageIndex - 1;

      if (rightExpandedIndex >= 0 &&
          rightExpandedIndex < expandedPages.length &&
          expandedPages[rightExpandedIndex].type === 'unfold-right') {
        setVisibleUnfoldData({
          layout: expandedPages[rightExpandedIndex].unfoldData!.layout,
          pdfs: expandedPages[rightExpandedIndex].unfoldData!.pdfs,
        });
      }
    }
  }, [expandedPages]);

  const handleFlip = (e: any) => {
    const newPageIndex = e.data;
    setCurrentPage(newPageIndex + 1);

    // Detect if current visible spread includes unfold pages
    // newPageIndex is the left page of the current spread (0-indexed)
    // Adjusted indices: cover (0), blank (1), then expandedPages start
    const rightExpandedIndex = newPageIndex - 1; // Right page in expandedPages array

    if (rightExpandedIndex >= 0 &&
        rightExpandedIndex < expandedPages.length &&
        expandedPages[rightExpandedIndex].type === 'unfold-right') {
      setVisibleUnfoldData({
        layout: expandedPages[rightExpandedIndex].unfoldData!.layout,
        pdfs: expandedPages[rightExpandedIndex].unfoldData!.pdfs,
      });
    } else {
      setVisibleUnfoldData(null);
    }
  };

  const openGatefold = (pdfs: string[], currentFlipbookPage: number) => {
    setGatefoldState({
      isActive: true,
      pdfs,
      returnToPage: currentFlipbookPage,
      transitionPhase: 'closing-flipbook',
    });

    // Sequence: close flipbook → open gatefold
    setTimeout(() => {
      setGatefoldState((prev) => ({ ...prev, transitionPhase: 'opening-gatefold' }));
    }, FLIPBOOK_FADE_DURATION);

    setTimeout(() => {
      setGatefoldState((prev) => ({ ...prev, transitionPhase: 'active' }));
    }, FLIPBOOK_FADE_DURATION + GATEFOLD_ANIMATION_DURATION);
  };

  const closeGatefold = () => {
    setGatefoldState((prev) => ({ ...prev, transitionPhase: 'closing-gatefold' }));

    setTimeout(() => {
      setGatefoldState((prev) => ({ ...prev, transitionPhase: 'opening-flipbook' }));
    }, GATEFOLD_ANIMATION_DURATION);

    setTimeout(() => {
      setGatefoldState({
        isActive: false,
        pdfs: [],
        returnToPage: 0,
        transitionPhase: 'idle',
      });
    }, GATEFOLD_ANIMATION_DURATION + FLIPBOOK_FADE_DURATION);
  };

  const handleExpandUnfold = (pdfs: string[]) => {
    openGatefold(pdfs, currentPage);
  };

  const handlePrevious = () => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipPrev();
    }
  };

  const handleNext = () => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipNext();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-2xl text-gray-600">{t('pdf-loading', languageState)}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6">
        <p className="text-2xl text-red-600 mb-4">{t('pdf-error', languageState)}</p>
        <p className="text-base text-gray-500">{error.message}</p>
      </div>
    );
  }

  const isFlipbookVisible =
    !gatefoldState.isActive ||
    gatefoldState.transitionPhase === 'closing-flipbook' ||
    gatefoldState.transitionPhase === 'closing-gatefold' ||
    gatefoldState.transitionPhase === 'opening-flipbook';

  const isGatefoldVisible =
    gatefoldState.isActive &&
    (gatefoldState.transitionPhase === 'opening-gatefold' ||
      gatefoldState.transitionPhase === 'active' ||
      gatefoldState.transitionPhase === 'closing-gatefold');

  const flipbookOpacity =
    gatefoldState.transitionPhase === 'closing-flipbook' ? 0 :
    gatefoldState.transitionPhase === 'opening-flipbook' ? 1 : 1;

  return (
    <div className="flex flex-col items-center w-full relative">
      <div
        className="flipbook-container transition-opacity duration-150 relative"
        style={{
          opacity: flipbookOpacity,
          display: isFlipbookVisible ? 'block' : 'none',
        }}
      >
        {/* @ts-ignore - react-pageflip types are incomplete */}
        <HTMLFlipBook
          ref={flipBookRef}
          width={pageWidth}
          height={pageHeight}
          size="fixed"
          minWidth={pageWidth}
          maxWidth={pageWidth}
          minHeight={pageHeight}
          maxHeight={pageHeight}
          drawShadow={true}
          flippingTime={1000}
          usePortrait={false}
          startZIndex={0}
          autoSize={false}
          maxShadowOpacity={0.5}
          showCover={true}
          mobileScrollSupport={true}
          onFlip={handleFlip}
          className="shadow-2xl"
        >
          {/* Cover page */}
          {pages.length > 0 && (
            <FlipPage
              key="cover"
              pdfUrl={isSinglePage(pages[0]) ? pages[0].url : pages[0].pdfs[0]}
              pageNumber={1}
              width={pageWidth}
              density="hard"
            />
          )}
          {/* Blank page after cover to fix pagination */}
          <FlipPage
            key="blank"
            pageNumber={1}
            width={pageWidth}
            isBlank={true}
          />
          {/* Rest of the pages (expanded) */}
          {expandedPages.map((page, index) => {
            if (page.type === 'unfold-right') {
              return (
                <UnfoldableFlipPage
                  key={`unfold-right-${index}`}
                  pdfUrl={page.url}
                  width={pageWidth}
                  showUnfoldIcon={false}
                  onExpand={() => handleExpandUnfold(page.unfoldData!.pdfs)}
                  layout={page.unfoldData!.layout}
                />
              );
            } else if (page.type === 'unfold-left') {
              return (
                <FlipPage
                  key={`unfold-left-${index}`}
                  pdfUrl={page.url}
                  pageNumber={1}
                  width={pageWidth}
                />
              );
            } else {
              return (
                <FlipPage
                  key={`page-${index}`}
                  pdfUrl={page.url}
                  pageNumber={1}
                  width={pageWidth}
                />
              );
            }
          })}
        </HTMLFlipBook>

        {/* Centered unfold icon overlay */}
        {visibleUnfoldData && (
          <CenteredUnfoldIcon
            layout={visibleUnfoldData.layout}
            onExpand={() => handleExpandUnfold(visibleUnfoldData.pdfs)}
            pageWidth={pageWidth}
            isVisible={!gatefoldState.isActive}
          />
        )}
      </div>

      {/* Navigation arrows - positioned outside flipbook */}
      {isFlipbookVisible && currentPage > 1 && (
        <button
          onClick={handlePrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-2xl hover:bg-gray-100 transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-8 h-8 text-gray-700" />
        </button>
      )}

      {isFlipbookVisible && currentPage < expandedPages.length + 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-2xl hover:bg-gray-100 transition-colors"
          aria-label="Next page"
        >
          <ChevronRight className="w-8 h-8 text-gray-700" />
        </button>
      )}

      {/* Gatefold view */}
      {isGatefoldVisible && (
        <GatefoldView
          pdfs={gatefoldState.pdfs}
          onClose={closeGatefold}
          transitionPhase={
            gatefoldState.transitionPhase === 'opening-gatefold' ? 'opening' :
            gatefoldState.transitionPhase === 'closing-gatefold' ? 'closing' : 'active'
          }
        />
      )}
    </div>
  );
};
